import { useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { MissingResourceError } from '#api/errors.ts';
import { useSuspenseHostQuery } from '#api/hosts.queries.ts';
import { useSuspenseNetworkQuery } from '#api/networks.queries.ts';
import { maxRoutesQueryOptions } from '#api/routes.queries.ts';
import { maxTagsQueryOptions } from '#api/tags.queries.ts';
import { BlockIcon } from '#assets/ReactSvg.ts';
import { Banner } from '#components/atoms/banner/Banner.tsx';
import { InlineButton } from '#components/atoms/buttons/inline-button/InlineButton.tsx';
import { Link } from '#components/atoms/link/Link.tsx';
import { CopyPill } from '#components/atoms/pill/copy-pill/CopyPill.tsx';
import { HostButtons } from '#components/patterns/host-buttons/HostButtons.tsx';
import { MainLayout } from '#components/patterns/layouts/main/MainLayout.tsx';
import { PageHeader } from '#components/patterns/page-header/PageHeader.tsx';
import { useAppLocation } from '#hooks/useAppLocation.ts';
import { usePersistedNetworkFilter } from '#hooks/usePersistedNetworkFilter.tsx';
import { usePrefetchSuspenseQuery } from '#hooks/usePrefetchSuspenseQuery.ts';
import { hostsListTo } from '#screens/hosts/listLocation.ts';
import { DeleteLighthouseModal } from '#screens/lighthouses/delete/DeleteLighthouseModal.tsx';
import { DeleteRelayDialog } from '#screens/relays/delete/DeleteRelayDialog.tsx';
import { hostKind } from '#util/host.ts';
import { fromArray, Titled } from '#util/title.ts';
import { AddHostIPDialog } from '../add-ip/AddHostIPDialog.tsx';
import { BlockHostDialog } from '../block/BlockHostDialog.tsx';
import { DeleteHostDialog } from '../delete/DeleteHostDialog.tsx';
import { UnblockHostDialog } from '../unblock/UnblockHostDialog.tsx';
import { AdvancedConfigCard } from './advanced-config-card/AdvancedConfigCard.tsx';
import { EffectiveAccessCard } from './effective-access-card/EffectiveAccessCard.tsx';
import { FirewallCard } from './firewall-card/FirewallCard.tsx';
import { HostHeader } from './header/HostHeader.tsx';
import { HostLogs } from './host-logs/HostLogs.tsx';

/**
 * Detail page for all host kinds. The kind — and with it the breadcrumb target, blocked-banner
 * copy, and delete dialog — is derived from the fetched host's type flags, not from which route
 * rendered the page.
 */
export function HostDetailPage() {
  const { hostId } = useParams() as { hostId: string };
  {
    usePrefetchSuspenseQuery(maxTagsQueryOptions);
    usePrefetchSuspenseQuery(maxRoutesQueryOptions);
  }
  const {
    data: { data: host },
    error,
    isFetching,
    dataUpdatedAt,
  } = useSuspenseHostQuery(hostId);
  const {
    data: { data: network },
  } = useSuspenseNetworkQuery(host.networkID);
  const navigate = useNavigate();
  const location = useAppLocation();
  const [isDeleting, setIsDeleting] = useState<boolean>(false);
  const [isBlocking, setIsBlocking] = useState<boolean>(false);
  const [isUnblocking, setIsUnblocking] = useState<boolean>(false);
  const [isAddingIPv4, setIsAddingIPv4] = useState<boolean>(false);
  const persistedNetworkFilter = usePersistedNetworkFilter();

  const kind = hostKind(host);
  const listTo = hostsListTo(kind, persistedNetworkFilter);

  // Check if this has been deleted (e.g. in a different tab)
  if (error && !isFetching && error instanceof MissingResourceError) {
    throw error;
  }

  const deleteDialogProps = {
    onDelete: () => {
      void navigate(location.state?.from ?? listTo, { replace: true });
    },
    onCancel: () => {
      setIsDeleting(false);
    },
  };

  return (
    <Titled title={(title) => fromArray([host.name, title])}>
      <MainLayout
        header={
          <PageHeader
            title={
              <>
                <Link to={listTo} className="a">
                  Hosts
                </Link>
                {' / '}
                <span>{host.name}</span>
              </>
            }
            subtitle={
              <span className="mb-8">
                ID: <CopyPill value={host.id} />
              </span>
            }
          >
            <HostButtons
              host={host}
              onDeleteClick={() => setIsDeleting(true)}
              onBlockClick={() => setIsBlocking(true)}
              onUnblockClick={() => setIsUnblocking(true)}
            />
          </PageHeader>
        }
      >
        {host.isBlocked ?
          <Banner className="mb-24" Icon={BlockIcon} variant="alert">
            This {kind} is blocked and can&rsquo;t communicate with other hosts.{' '}
            <InlineButton
              onClick={() => {
                setIsUnblocking(true);
              }}
            >
              Unblock {kind}
            </InlineButton>
          </Banner>
        : null}
        <HostHeader
          host={host}
          network={network}
          dataUpdatedAt={new Date(dataUpdatedAt)}
          className="mb-40"
          onAddIPv4Click={() => setIsAddingIPv4(true)}
          canEditTags={true}
        />
        <AdvancedConfigCard host={host} className="mb-40" />
        <FirewallCard host={host} className="mb-40" />
        <EffectiveAccessCard host={host} className="mb-40" />
        <HostLogs hostId={host.id} className="mb-40" />

        {isDeleting ?
          kind === 'lighthouse' ?
            <DeleteLighthouseModal lighthouseId={hostId} {...deleteDialogProps} />
          : kind === 'relay' ?
            <DeleteRelayDialog relayId={hostId} {...deleteDialogProps} />
          : <DeleteHostDialog hostId={hostId} {...deleteDialogProps} />
        : null}
        {isBlocking ?
          <BlockHostDialog
            hostId={hostId}
            onClose={() => {
              setIsBlocking(false);
            }}
          />
        : null}
        {isUnblocking ?
          <UnblockHostDialog
            hostId={hostId}
            onClose={() => {
              setIsUnblocking(false);
            }}
          />
        : null}
        {isAddingIPv4 ?
          <AddHostIPDialog
            hostId={hostId}
            network={network}
            onClose={() => {
              setIsAddingIPv4(false);
            }}
          />
        : null}
      </MainLayout>
    </Titled>
  );
}
